import React, { useState } from "react";
import PropTypes from "prop-types";
import { HiChevronRight, HiChevronDown } from "react-icons/hi2";
import AxaoLogo from "../assets/AXAO.svg";
import FoonLogo from "../assets/FOON.svg";
import StareIcon from "../assets/STARE.svg";
import BocceIcon from "../assets/BOCCE.svg";
import YT from "../assets/YT.svg";
import IG from "../assets/IG.svg";
import { useNavigate } from "react-router-dom";
import { buildFoonPath } from "../utils/routes";

const footerLinks = {
  store: ["Earbuds", "Headphones", "Speakers", "Accessories", "Shop All"],
  support: [
    "Contact Us",
    "Help Center",
    "My Orders",
    "Returns & Warranty",
    "Shipping & Delivery",
  ],
  company: ["Account", "Careers", "Community", "Our Mission", "Partnerships"],
};

const AccordionSection = ({ title, links, onLinkClick }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className=""
      style={{
        borderBottom: "1px solid #DDDCDC",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-4 text-left"
      >
        <span className="text-[11px] font-mono uppercase text-[#6d6d6d]">
          {title}
        </span>
        {open ? (
          <HiChevronDown className="text-[#6d6d6d]" size={11} />
        ) : (
          <HiChevronRight className="text-[#6d6d6d]" size={11} />
        )}
      </button>
      {open && (
        <ul className="pb-4 space-y-3 pl-1">
          {links.map((link) => (
            <li
              key={link}
              onClick={() => onLinkClick(title, link)}
              className="text-[14px] font-light hover:text-gray-500 cursor-pointer"
            >
              {link}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

AccordionSection.propTypes = {
  title: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLinkClick: PropTypes.func.isRequired,
};

const Footer = () => {
  const navigate = useNavigate();

  const handleFooterLinkClick = (section, link) => {
    if (section !== "store") {
      return;
    }

    if (link === "Shop All") {
      navigate(buildFoonPath("/store"));
      return;
    }

    const category = link.toLowerCase();
    navigate(`${buildFoonPath("/store")}?category=${encodeURIComponent(category)}`);
  };

  return (
    <footer className="bg-[#ececec] py-14 lg:py-24 px-6 md:px-8 lg:px-16 text-black">
      <div className="flex flex-col lg:flex-row justify-between gap-10 lg:gap-16 mb-10 lg:mb-20">
        {/* Newsletter Section */}
        <div className="max-w-[450px]">
          <img
            src={FoonLogo}
            alt="Foon Logo"
            className="mb-8 lg:mb-12 w-[63px] h-[16px] lg:w-[110px] lg:h-[29px]"
          />
          <p className="text-[14px] lg:text-[16px] font-light leading-relaxed mb-1">
            Subscribe to our newsletter.
          </p>

          <div className="relative h-[53px] lg:h-[69px] w-full flex items-center">
            <input
              type="email"
              placeholder="Enter email address"
              className="bg-[#ECECEC] px-4 lg:px-8 py-3 lg:py-4 w-full h-full text-[12px] lg:text-[14px] font-helvetical font-light placeholder-gray-400 border border-[#7A7A7A] border-r-transparent"
            />
            <button className="px-6 lg:px-10 py-3 lg:py-4 h-full font-helvetical border border-[#7A7A7A] bg-[#ECECEC] text-[12px] lg:text-[14px] hover:bg-black hover:text-white transition-all whitespace-nowrap">
              Submit
            </button>
          </div>

          <p className="text-[12px] lg:text-[14px] text-[#6d6d6d] mt-1 font-light">
            By signing up, I agree with the data protection policy.
          </p>
        </div>

        {/* Links: Accordion on mobile, columns on desktop */}
        <div className="lg:hidden">
          {Object.entries(footerLinks).map(([title, links]) => (
            <AccordionSection
              key={title}
              title={title}
              links={links}
              onLinkClick={handleFooterLinkClick}
            />
          ))}
        </div>

        <div className="hidden lg:flex flex-wrap gap-12 lg:gap-24">
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title} className="min-w-[160px]">
              <h4 className="text-[12px] font-mono uppercase text-[#8b8b8b] mb-8">
                {title}
              </h4>
              <ul className="space-y-4">
                {links.map((link) => (
                  <li
                    key={link}
                    onClick={() => handleFooterLinkClick(title, link)}
                    className="text-[16px] font-light hover:text-gray-500 cursor-pointer"
                  >
                    {link}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div>
        <div className="h-px w-full bg-[#DDDCDC] mb-6 lg:mb-10 max-lg:!hidden" />

        {/* Desktop bottom bar */}
        <div className="hidden lg:flex justify-between w-full items-center shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex gap-4">
              <img
                src={IG}
                alt="IG logo"
                onClick={() =>
                  window.open(
                    "https://www.instagram.com/foon.audio?igsh=MXZubjFrY29vbmpqcg==",
                    "_blank",
                  )
                }
              />
              <img src={YT} alt="YT logo" />
            </div>
            <div className="flex gap-4">
              <img src={AxaoLogo} alt="Axao logo" />
              <img src={FoonLogo} alt="Foon logo" />
              <img src={StareIcon} alt="Stare logo" />
              <img src={BocceIcon} alt="Bocce logo" />
            </div>
          </div>

          <p className="text-[12px] font-mono font-light text-[#6d6d6d] uppercase">
            Terms of Use
          </p>

          <p className="text-[12px] font-mono font-light text-[#6d6d6d] uppercase">
            Privacy Policy
          </p>

          <p className="text-[12px] font-mono font-light text-[#6d6d6d] uppercase">
            © 2026 FOON AUDIO
          </p>

          {/* <div className="flex flex-row-reverse w-full whitespace-nowrap">
            <div className="flex items-center gap-4 text-[12px] font-light font-mono text-[#6d6d6d] uppercase">
              <span className="hover:text-black cursor-pointer">
                Terms & Conditions
              </span>
              <span className="hover:text-black cursor-pointer">
                Cookies Policy
              </span>
              <span className="hover:text-black cursor-pointer">
                Privacy Policy
              </span>
            </div>
          </div> */}
        </div>

        {/* Mobile bottom bar */}
        <div className="flex flex-col gap-6 lg:!hidden">
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              <img
                src={IG}
                alt="IG logo"
                onClick={() =>
                  window.open(
                    "https://www.instagram.com/foon.audio?igsh=MXZubjFrY29vbmpqcg==",
                    "_blank",
                  )
                }
              />
              <img src={YT} alt="YT logo" />
            </div>
            <div className="flex gap-3 items-center">
              <img src={AxaoLogo} alt="Axao logo" />
              <img src={FoonLogo} alt="Foon logo" />
              <img src={StareIcon} alt="Stare logo" />
              <img src={BocceIcon} alt="Bocce logo" />
            </div>
          </div>

          <div className="h-px w-full bg-[#DDDCDC]" />

          <div className="flex justify-between w-full shrink whitespace-nowrap ">
            {/* <div className="flex items-center gap-2 text-[10px] font-light font-mono text-[#6d6d6d] uppercase flex-wrap">
              <span className="hover:text-black cursor-pointer">
                Terms & Conditions
              </span>
              <span className="w-px h-[10px] bg-[#6d6d6d]" />
              <span className="hover:text-black cursor-pointer">
                Cookies Policy
              </span>
              <span className="w-px h-[10px] bg-[#6d6d6d]" />
              <span className="hover:text-black cursor-pointer">
                Privacy Policy
              </span>
            </div>
            <p className="text-[10px] font-mono font-light text-[#6d6d6d] uppercase">
              © 2026 FOON AUDIO, All rights reserved.
            </p> */}
            <p className="text-[10px] font-mono font-light text-[#6d6d6d] uppercase">
              Terms of Use
            </p>

            <p className="text-[10px] font-mono font-light text-[#6d6d6d] uppercase">
              Privacy Policy
            </p>

            <p className="text-[10px] font-mono font-light text-[#6d6d6d] uppercase">
              © 2026 FOON AUDIO
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
